import { inject, injectable } from 'inversify';
import Request from '@framework/api/Rpc/Request';
import { type IDispatcher } from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr-internal';
import DITypes from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr-internal/DITypes';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import { ISector } from '@/creditflowresearch/domain/vo/ISector';
import {
  IDictionaryBenchmark,
  IDictionaryBenchmarkGroup,
  IDictionaryCompanyRole,
  IDictionaryCouponType,
  IDictionaryCurrency,
  IDictionaryDealStatus,
  IDictionaryDocumentTypeGroup,
  IDictionaryEsgType,
  IDictionaryListing,
  IDictionaryMarket,
  IDictionaryMarketSegment,
  IDictionaryRegistration,
  IDictionaryRegistrationGroup,
  IDictionaryRatingGroup,
  IDictionaryTimeZone,
  IDictionaryRanking,
  IDictionaryCountry,
  IDictionaryIndustry,
  IDictionaryUopType,
  IDictionarySizeDescription,
} from '@/creditflowresearch/domain/vo/IDictionary';
import {
  MarketDetectPayloadItem,
  MarketDetectResponseItem,
} from '@/condor/domain/vo/Finsight/Deal/TranchesMarketSegment';
import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import { IMarketTheme, IMarketThemeGroup } from '@/creditflowresearch/domain/vo/Dictionary/MarketThemes';
import { IRatingAgency } from '@/creditflowresearch/domain/vo/RatingAgency/IRatingAgency';
import { IHeaderMenuItems } from '@/creditflowresearch/domain/vo/IHeaderMenuItems';
import { withClientCache } from '@/creditflowresearch/infrastructure/cache/withClientCache';
import { withServerCache } from '@/creditflowresearch/infrastructure/cache/withServerCache';

@injectable()
class DictionaryRepository {
  constructor(
    @inject(DITypes.JsonRpcDispatcher) protected readonly rpc: IDispatcher,
  ) {}

  @withServerCache({ scope: 'Dictionary' })
  async getMarkets(payload = {}): Promise<IDictionaryMarket[]> {
    let req = new Request('cfr.dictionary.get_markets', payload);

    let response = await this.rpc.call<RpcSuccess>(req);
    return response.getResult().payload;
  }

  @withServerCache({ scope: 'Dictionary' })
  async getMarketSegments(payload = {}): Promise<IDictionaryMarketSegment[]> {
    let req = new Request('cfr.dictionary.get_market_segments', payload);

    let response = await this.rpc.call<RpcSuccess>(req);
    return response.getResult().payload;
  }

  async getMarketDetect(payload: MarketDetectPayloadItem[]): Promise<MarketDetectResponseItem[]> {
    const request = new Request('cfr.internal.deal.market.detect', { collection: payload });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  @withServerCache({ scope: 'Dictionary' })
  async getThemes(payload: { isGrouped?: boolean } = {}): Promise<IMarketTheme[] | IMarketThemeGroup[]> {
    const request = new Request('cfr.dictionary.get_themes', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getDealStatuses(): Promise<IDictionaryDealStatus[]> {
    const req = new Request('cfr.dictionary.get_deal_statuses', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withServerCache({ scope: 'Dictionary' })
  async getTimeZones(): Promise<IDictionaryTimeZone[]> {
    const req = new Request('cfr.dictionary.get_time_zones', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getCurrencies(): Promise<IDictionaryCurrency[]> {
    const req = new Request('cfr.dictionary.get_currencies', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withServerCache({ scope: 'Dictionary' })
  async getBenchmarks(): Promise<IDictionaryBenchmark[]> {
    const req = new Request('cfr.dictionary.get_benchmarks', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getCouponTypes(): Promise<IDictionaryCouponType[]> {
    const req = new Request('cfr.dictionary.get_coupon_types', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getCompanyRoles(): Promise<IDictionaryCompanyRole[]> {
    const req = new Request('cfr.dictionary.get_company_roles', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withServerCache({ scope: 'Dictionary' })
  async getRegistration(): Promise<IDictionaryRegistration[]> {
    const req = new Request('cfr.dictionary.get_registrations', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withServerCache({ scope: 'Dictionary' })
  async getProducts(payload = {}): Promise<[IProduct?]> {
    const req = new Request('cfr.dictionary.get_products', payload);

    const response = await this.rpc.call<RpcSuccess>(req);
    return response.getResult().payload;
  }

  @withServerCache({ scope: 'Dictionary' })
  async getRegions(payload = {}): Promise<[IRegion?]> {
    const req = new Request('cfr.dictionary.get_regions', payload);

    const response = await this.rpc.call<RpcSuccess>(req);
    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getRankings(payload = {}): Promise<[IDictionaryRanking?]> {
    const req = new Request('cfr.dictionary.get_rankings', payload);

    const response = await this.rpc.call<RpcSuccess>(req);
    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getRatingAgencies(payload = {}): Promise<IRatingAgency[]> {
    const req = new Request('cfr.dictionary.get_rating_agencies', payload);

    const response = await this.rpc.call<RpcSuccess>(req);
    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getCountries(payload = {}): Promise<IDictionaryCountry[]> {
    const req = new Request('cfr.dictionary.get_countries', payload);

    const response = await this.rpc.call<RpcSuccess>(req);
    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getSectors(): Promise<ISector[]> {
    const req = new Request('cfr.dictionary.get_sectors', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getEsgTypes(): Promise<IDictionaryEsgType[]> {
    const req = new Request('cfr.dictionary.get_esg_types', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getIndustries(): Promise<IDictionaryIndustry[]> {
    const req = new Request('cfr.dictionary.get_industries', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getHeaderMenuItems(): Promise<IHeaderMenuItems> {
    const request = new Request('cfr.internal.header_menu.get_items', {});
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getBenchmarkGroups(): Promise<IDictionaryBenchmarkGroup[]> {
    const req = new Request('cfr.dictionary.get_benchmark_groups', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getRatingGroups(payload = {}): Promise<IDictionaryRatingGroup[]> {
    const req = new Request('cfr.dictionary.get_rating_groups', payload);
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getRegistrationGroups(): Promise<IDictionaryRegistrationGroup[]> {
    const req = new Request('cfr.dictionary.get_registration_groups', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getListings(): Promise<IDictionaryListing[]> {
    const req = new Request('cfr.dictionary.get_listings', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getUopTypes(): Promise<IDictionaryUopType[]> {
    const req = new Request('cfr.dictionary.get_uop_types', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getSizeDescriptions(): Promise<IDictionarySizeDescription[]> {
    const req = new Request('cfr.dictionary.get_size_descriptions', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withServerCache({ scope: 'Dictionary' })
  async getDocumentTypeGroups(): Promise<IDictionaryDocumentTypeGroup[]> {
    const req = new Request('cfr.dictionary.get_document_type_groups', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  @withClientCache
  @withServerCache({ scope: 'Dictionary' })
  async getDebutTypes(): Promise<{ id: string, name: string, priority: number }[]> {
    const req = new Request('cfr.dictionary.get_debut_types', {});
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }
}

export default DictionaryRepository;
